import { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import {
  Divider,
  Tooltip,
  ResetViewIcon,
  SelectionToolIcon,
  MeasureIcon,
  OutlineCircleQuestionIcon,
  AxisAlignedDimensionsIcon,
  OptimalBoundingBoxIcon,
} from '@frontend/legacy-components';

import { featureFlagKeys } from 'modules/common/drawers/featureFlagDrawer/const/featureFlagKeys';
import CrossSectionTool from 'modules/common/viewer/cadViewer/addOns/CrossSectionToolBar';
import ExplodedViewTool from 'modules/common/viewer/cadViewer/addOns/ExplodedViewToolBar';
import FocusedViewTool from 'modules/common/viewer/cadViewer/addOns/FocusedViewTool';
import HighlightViewTool from 'modules/common/viewer/cadViewer/addOns/HighlightViewTool';
import PMIViewTool from 'modules/common/viewer/cadViewer/addOns/PMIViewTool';
import WireFrameTool from 'modules/common/viewer/cadViewer/addOns/WireFrameTool';
import CADViewerDisplayOptionsPopover from 'modules/common/viewer/cadViewer/components/CADViewerDisplayOptionsPopover';
import {
  resetView,
  selectorTool,
  measurementTool,
  dimensionTool,
  optimalBoundingBoxTool,
} from 'modules/common/viewer/cadViewer/data/actions';
import {
  getShowDxfControls,
  getIsSelectorToolActive,
  getIsMeasurementToolActive,
  getIsDimensionToolActive,
  getIsOptimalBoundingBoxToolActive,
} from 'modules/common/viewer/cadViewer/data/reducer';
import {
  ViewerPopoverButton,
  ViewerButton,
  ViewerButtonTooltip,
} from 'modules/common/viewer/components';
import {
  getDoesActiveFileHaveOptimalBoundingBox,
  getIsActiveFileAssembly,
} from 'modules/common/viewer/data/reducer';
import { useIsActiveFile3DRenderable } from 'modules/common/viewer/hooks/useActiveViewerKind';

import { useFeatureFlagValue } from 'store/session/hooks';

import { isMobileBrowser } from 'utils/browserUtils';

import { ViewerToolsBar } from '../components/ViewerToolsBar';
import {
  Controls2DRows,
  Controls3DBody,
} from './components/CADViewerControlContents';

const CADViewerTools = () => {
  const dispatch = useDispatch();
  const isMobile = isMobileBrowser();
  const doesActiveFileHaveOptimalBoundingBox = useSelector(
    getDoesActiveFileHaveOptimalBoundingBox,
  );
  const isEnabled = useIsActiveFile3DRenderable();
  const isActiveFileAssembly = useSelector(getIsActiveFileAssembly);
  const showDxfControls = useSelector(getShowDxfControls);

  /** 3D-only viewer tools (not used for flat DXF–DWG / 2D viewing) */
  const isSolid3dToolbarEnabled = Boolean(isEnabled) && !showDxfControls;

  const isPMIEnabled = useFeatureFlagValue(
    featureFlagKeys.isPmiRenderingEnabled,
  );

  /* Selector Tool */
  const isSelectorToolActive = useSelector(getIsSelectorToolActive);
  const toggleSelectorTool = useCallback(() => {
    dispatch(selectorTool.toggle());
  }, [dispatch]);

  /* Measurement Tool */
  const isMeasurementToolActive = useSelector(getIsMeasurementToolActive);
  const toggleMeasurementTool = useCallback(() => {
    dispatch(measurementTool.toggle());
  }, [dispatch]);

  /* Axis Aligned Dimensions Tool */
  const isAxisAlignedDimensionToolActive = useSelector(
    getIsDimensionToolActive,
  );
  const toggleDimensionTool = useCallback(() => {
    dispatch(dimensionTool.toggle());
  }, [dispatch]);

  /* Optimal Bounding Box Tool */
  const isOptimalBoundingBoxToolActive = useSelector(
    getIsOptimalBoundingBoxToolActive,
  );
  const toggleOptimalBoundingBoxTool = useCallback(() => {
    dispatch(optimalBoundingBoxTool.toggle());
  }, [dispatch]);

  /* Reset Tool */
  const handleResetView = useCallback(() => {
    dispatch(resetView());
  }, [dispatch]);

  return (
    <>
      <ViewerToolsBar>
        {/* Selector Tool */}
        {isEnabled && (
          <Tooltip
            placement="bottom"
            label={
              <ViewerButtonTooltip
                firstLine="Select"
                secondLine="Ctrl + drag LMB to box select"
              />
            }
          >
            <ViewerButton
              isActive={isSelectorToolActive}
              aria-label="Select"
              onClick={toggleSelectorTool}
            >
              <SelectionToolIcon boxSize={3} />
            </ViewerButton>
          </Tooltip>
        )}
        {/* Measurement Tool */}
        {isEnabled && (
          <Tooltip
            placement="bottom"
            label={<ViewerButtonTooltip firstLine="Measure" />}
          >
            <ViewerButton
              isActive={isMeasurementToolActive}
              aria-label="Measure"
              onClick={toggleMeasurementTool}
            >
              <MeasureIcon boxSize={3} />
            </ViewerButton>
          </Tooltip>
        )}
        <Divider orientation="vertical" h={4} />
        {/* Axis Aligned Dimensions Tool */}
        {isEnabled && (
          <Tooltip
            placement="bottom"
            label={
              <ViewerButtonTooltip
                firstLine="Axis aligned dimensions"
                secondLine="All visible bodies"
              />
            }
          >
            <ViewerButton
              isActive={isAxisAlignedDimensionToolActive}
              aria-label="Axis aligned dimensions"
              onClick={toggleDimensionTool}
            >
              <AxisAlignedDimensionsIcon boxSize={3} />
            </ViewerButton>
          </Tooltip>
        )}
        {/* Optimal Bounding Box Tool */}
        {isEnabled && doesActiveFileHaveOptimalBoundingBox && (
          <Tooltip
            placement="bottom"
            label={
              <ViewerButtonTooltip
                firstLine="Optimal bounding box dimensions"
                secondLine="Focused bodies"
              />
            }
          >
            <ViewerButton
              isActive={isOptimalBoundingBoxToolActive}
              aria-label="Optimal bounding box dimensions"
              onClick={toggleOptimalBoundingBoxTool}
            >
              <OptimalBoundingBoxIcon boxSize={3} />
            </ViewerButton>
          </Tooltip>
        )}
        <Divider orientation="vertical" h={4} />
        <PMIViewTool isEnabled={isEnabled && isPMIEnabled} />
        <WireFrameTool isEnabled={isSolid3dToolbarEnabled} />
        <HighlightViewTool isEnabled={isSolid3dToolbarEnabled} />
        <FocusedViewTool isEnabled={isSolid3dToolbarEnabled} />
        <Divider orientation="vertical" h={4} />
        <CrossSectionTool
          isEnabled={isSolid3dToolbarEnabled}
          placement="toolbar"
        />
        <ExplodedViewTool
          isCad3d={isSolid3dToolbarEnabled}
          isAssembly={isActiveFileAssembly}
          placement="toolbar"
        />
        {isSolid3dToolbarEnabled && <Divider orientation="vertical" h={4} />}
        {/* Reset View Tool */}
        {isEnabled && (
          <Tooltip
            placement="bottom"
            label={<ViewerButtonTooltip firstLine="Reset view" />}
          >
            <ViewerButton aria-label="Reset view" onClick={handleResetView}>
              <ResetViewIcon boxSize={3} />
            </ViewerButton>
          </Tooltip>
        )}
        {/* Controls Info Tool */}
        {isEnabled && (
          <ViewerPopoverButton
            title="Viewer controls"
            icon={<OutlineCircleQuestionIcon boxSize={3} />}
          >
            {showDxfControls ? (
              <Controls2DRows isMobile={isMobile} />
            ) : (
              <Controls3DBody isMobile={isMobile} />
            )}
          </ViewerPopoverButton>
        )}
        {/* Display Options Tool */}
        {isEnabled && (
          <CADViewerDisplayOptionsPopover showDxfControls={showDxfControls} />
        )}
      </ViewerToolsBar>

      <ExplodedViewTool
        isCad3d={isSolid3dToolbarEnabled}
        isAssembly={isActiveFileAssembly}
        placement="floating"
      />
      <CrossSectionTool
        isEnabled={isSolid3dToolbarEnabled}
        placement="floating"
      />
    </>
  );
};

export default CADViewerTools;
